import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CommandLineIcon,
  CpuChipIcon,
  KeyIcon,
  ClockIcon,
  CheckIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
  HandRaisedIcon,
  LockClosedIcon,
} from 'components/icons';

import { waitlistApi } from '../services/api';

// =============================================================================
// Clevor for Agents — Phase 0
// =============================================================================
//
// This page describes something that does not exist yet, which makes it the
// exact shape of thing this codebase keeps getting wrong: a signup checkbox
// pointing at absent terms, a 7-day trial nobody offered, a list of API keys
// with no way to create one. The difference between that and an honest
// coming-soon page is entirely in the labelling, so the labelling is not
// decoration here.
//
// Rules this page follows:
//   - Every capability is in future tense or marked "planned".
//   - The setup snippet is an illustration. It is not copyable, and it says
//     so, because the endpoint it names would 404 today.
//   - The only working control on the page is the waitlist form.

const WAITLIST_SOURCE = 'agents-page';

// -----------------------------------------------------------------------------
// Planned tool surface
// -----------------------------------------------------------------------------
// Deliberately fewer than the 33–34 a competitor exposes, and weighted away from
// search. Reasoning is in docs/AGENT_SURFACE_SCOPE.md: search is a commodity we
// would lose at, and the execution-layer tools are the ones nobody else can
// serve.
//
// This list must equal what the server actually registers. It has drifted twice
// already — a heading claiming twenty-one over a list of nineteen — and the
// failure is invisible from this file, so: `TOOLS` in
// backend/src/services/agentTools.ts is the source of truth, and the count in
// the heading is derived from this list rather than typed beside it.

type Tier = 'read' | 'write' | 'spend' | 'approval';

// Live or not, per tool, and what each one costs. An undifferentiated list
// would read as though everything were free and safe, and three of these spend
// money. The unbuilt one stays listed — a roadmap is useful; a roadmap
// disguised as a product is not.
const TIERS: Record<Tier, { label: string; className: string }> = {
  read: { label: 'Live', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  write: { label: 'Live — changes your account', className: 'bg-blue-50 text-blue-700 ring-blue-200' },
  spend: {
    label: 'Live — spends 1 AI credit',
    className: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  approval: {
    label: 'Not open yet',
    className: 'bg-purple-50 text-purple-700 ring-purple-200',
  },
};


const TOOL_GROUPS: {
  name: string;
  note: string;
  tools: { name: string; desc: string; tier: Tier }[];
}[] = [
  {
    name: 'Discovery',
    note: 'Federal solicitations from SAM.gov.',
    tools: [
      { name: 'search_opportunities', desc: 'Open federal solicitations by NAICS codes, states, agencies and sub-agencies, set-asides, notice type or keyword — each a list, sorted by posted or due date', tier: 'read' },
      { name: 'list_agencies', desc: "Every agency and sub-agency as SAM.gov names them, with open counts — so a model filters by the real name, not a guess", tier: 'read' },
      { name: 'get_opportunity', desc: 'One solicitation in full — description, deadline, place of performance, contact', tier: 'read' },
      { name: 'get_watchlist', desc: 'What you are tracking, with days left on each', tier: 'read' },
      { name: 'add_to_watchlist', desc: 'Start tracking a solicitation; idempotent, so a repeated sweep is safe', tier: 'write' },
      { name: 'remove_from_watchlist', desc: 'Stop tracking one', tier: 'write' },
    ],
  },
  {
    name: 'Scouting',
    note: 'Watch for solicitations that fit, before anything is in the pipeline.',
    tools: [
      { name: 'get_watch_criteria', desc: 'What is being watched for: NAICS codes, states, agencies, set-asides, keywords', tier: 'read' },
      { name: 'set_watch_criteria', desc: 'Change it. Every new SAM.gov posting that fits all of it is recorded after each hourly import', tier: 'write' },
      { name: 'list_matches', desc: 'The scouting inbox — new matches with the reasons each one fit, newest first', tier: 'read' },
      { name: 'dismiss_match', desc: 'Set one aside, or put it back', tier: 'write' },
    ],
  },
  {
    name: 'Readiness',
    note: 'The part your agent cannot get from a contract database.',
    tools: [
      { name: 'get_company_profile', desc: 'Your NAICS codes, certifications, registration state and credit balance', tier: 'read' },
      { name: 'check_eligibility', desc: 'Whether you may bid: NAICS, set-aside certifications, deadline, place of performance', tier: 'read' },
      { name: 'check_bid_readiness', desc: 'Whether you are set up to — including a solicitation NAICS missing from your SAM registration', tier: 'read' },
      { name: 'get_credit_balance', desc: 'Credits left, what this connection has spent today, and what each paid tool costs', tier: 'read' },
    ],
  },
  {
    name: 'Documents',
    note: 'The solicitation itself, not a summary of it. This is what makes an extraction checkable.',
    tools: [
      { name: 'save_opportunity_documents', desc: "Fetch and extract every attachment SAM.gov holds — solicitation, SOW, wage determination, pricing sheet", tier: 'write' },
      { name: 'list_opportunity_documents', desc: 'What has been fetched, and whether the text is readable or the file was a scan', tier: 'read' },
      { name: 'read_document', desc: 'Read one, or search inside it — a term with zero matches is genuinely not in the document', tier: 'read' },
      { name: 'check_contract_structure', desc: 'Base period and option years, and whether your pricing covers all of them', tier: 'read' },
      { name: 'check_amendments', desc: 'Amendments issued since the original notice, and what each one changed', tier: 'read' },
      { name: 'get_bid_schedule', desc: "The agency's own pricing sheet, line by line, with what you have priced so far", tier: 'read' },
    ],
  },
  {
    name: 'Pipeline',
    note: 'What you are pursuing and where each one stands.',
    tools: [
      { name: 'get_pipeline', desc: 'Every pursuit and its stage, with counts across all eight stages', tier: 'read' },
      { name: 'add_to_pipeline', desc: 'Move a solicitation into pursuit', tier: 'write' },
      { name: 'update_pursuit_stage', desc: 'Advance or return a pursuit', tier: 'write' },
      { name: 'remove_from_pipeline', desc: 'Take one back out', tier: 'write' },
    ],
  },
  {
    name: 'Proposals',
    note: 'Reading a draft is free. Writing one still happens in Clevor, where you can see it.',
    tools: [
      { name: 'list_proposals', desc: 'Your drafts, their status and section counts', tier: 'read' },
      { name: 'get_proposal', desc: 'One draft and its sections; prose only when you ask for it', tier: 'read' },
      { name: 'get_compliance_matrix', desc: 'Every requirement, which section answers it, and what is still missing', tier: 'read' },
      { name: 'get_bid_checklist', desc: 'The answers the proposal writer was given — what separates a supported claim from an invented one', tier: 'read' },
      { name: 'list_past_performance', desc: 'Your past performance library with values, outcomes and CPARS ratings', tier: 'read' },
      { name: 'draft_proposal_section', desc: 'Write a section against the extracted requirements', tier: 'approval' },
    ],
  },
  {
    name: 'Analysis',
    note: 'The same agents the app runs, at the same cost, capped per connection per day.',
    tools: [
      { name: 'analyze_opportunity', desc: 'Requirements, evaluation criteria and deliverables out of the documents', tier: 'spend' },
      { name: 'find_teaming_partners', desc: 'Real firms with real UEIs, nearest the job site first', tier: 'spend' },
      { name: 'get_market_analysis', desc: 'Competition, incumbents and pricing history for an agency or a market', tier: 'spend' },
      { name: 'check_compliance', desc: 'Every hard requirement the solicitation imposes, each marked met, not met or unknown', tier: 'spend' },
      { name: 'get_agent_result', desc: 'Collect a finished analysis. Free — these take minutes, so they run in the background', tier: 'read' },
    ],
  },
];

// Counted, not typed. The heading and the list disagreed twice when the number
// was a literal, and nobody spots that by reading either one.
const LIVE_TOOL_COUNT = TOOL_GROUPS.reduce(
  (n, g) => n + g.tools.filter((t) => t.tier !== 'approval').length,
  0,
);

const SPELLED: Record<number, string> = {
  25: 'Twenty-five', 26: 'Twenty-six', 27: 'Twenty-seven', 28: 'Twenty-eight',
  29: 'Twenty-nine', 30: 'Thirty', 31: 'Thirty-one', 32: 'Thirty-two',
  33: 'Thirty-three', 34: 'Thirty-four', 35: 'Thirty-five', 36: 'Thirty-six',
};

// -----------------------------------------------------------------------------
// Clients
// -----------------------------------------------------------------------------
// One server, every client. Signing in is listed first and for almost every row
// because it is genuinely the better credential: scoped by what the person
// approved, expiring, rotating, revocable from a screen the agent cannot reach.
// A key is a static secret with none of that, which is why it is last and why
// its row names the only case that actually needs one — something running with
// no browser to sign in from.

const CLIENTS: {
  name: string;
  how: string;
  auth: 'key' | 'oauth';
}[] = [
  { name: 'ChatGPT', how: 'Settings → Connectors → Create', auth: 'oauth' },
  { name: 'claude.ai and Claude Desktop', how: 'Settings → Connectors → Add custom', auth: 'oauth' },
  { name: 'Claude Code', how: 'claude mcp add --transport http', auth: 'oauth' },
  { name: 'Antigravity CLI (Google)', how: 'Remote MCP server by URL', auth: 'oauth' },
  { name: 'Cursor, VS Code, Zed', how: 'MCP entry in the editor config', auth: 'oauth' },
  { name: 'Cron jobs, CI, servers', how: 'REST or MCP with a key in a header', auth: 'key' },
];


const GUARDRAILS = [
  {
    icon: KeyIcon,
    title: 'Keys you scope and revoke',
    body:
      'An agent key is not your password. It carries only the permissions you grant it, it is listed and revocable from your settings, and revoking it takes effect immediately.',
  },
  {
    icon: ClockIcon,
    title: 'A credit ceiling per connection',
    body:
      "Your balance says how much there is to spend. Nothing said how fast — and \u201ccheck everything I\u2019m watching\u201d is one instruction and forty credits. So each key and each connected app gets a daily ceiling you set, and spending is a permission you tick, never one that rides along.",
  },
  {
    icon: HandRaisedIcon,
    title: 'A refusal that means stop',
    body:
      'When a cap is reached, your agent gets an error that says do not retry and names the hour it lifts — not a failure it will read as bad luck and attempt again. Analyses run in the background and hand back a job to collect, so a slow one never leaves you billed for a result you never saw.',
  },
  {
    icon: ClipboardDocumentListIcon,
    title: 'One log, both kinds of actor',
    body:
      'Every agent call lands in the same audit log as your own actions, marked as the agent and the key that made it. You should never have to guess whether you did something or your agent did.',
  },
];

// -----------------------------------------------------------------------------

const Waitlist: React.FC<{ dark?: boolean }> = ({ dark = false }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await waitlistApi.join(email, WAITLIST_SOURCE);
      setSubmitted(true);
    } catch (err: any) {
      // Never claim success we did not get.
      setError(
        err?.response?.data?.error ||
          "That didn't go through. Please try again, or email support@clevor.ai.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`flex items-center justify-center gap-2 ${dark ? 'text-white' : 'text-content'}`}
      >
        <CheckIcon className={`h-5 w-5 ${dark ? 'text-green-300' : 'text-green-600'}`} />
        <span className="font-medium">
          On the list. We'll email you when the agent surface opens.
        </span>
      </motion.div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          disabled={submitting}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          aria-label="Email address for the agent surface waitlist"
          aria-invalid={!!error}
          className={
            dark
              ? 'w-full flex-1 rounded-lg border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/60 focus:border-transparent focus:ring-2 focus:ring-white/30 disabled:opacity-60'
              : 'input flex-1'
          }
        />
        <button
          type="submit"
          disabled={submitting}
          className={
            dark
              ? 'rounded-lg bg-white px-6 py-3 text-sm font-semibold text-brand-600 shadow-lg transition-colors hover:bg-brand-50 disabled:opacity-70'
              : 'btn-primary px-6 py-3 disabled:opacity-70'
          }
        >
          {submitting ? 'Adding you…' : 'Tell me when it opens'}
        </button>
      </form>
      {error && (
        <p
          role="alert"
          className={`mt-3 rounded-lg px-3 py-2 text-sm ${
            dark ? 'bg-red-500/25 text-white ring-1 ring-red-300/40' : 'bg-red-50 text-red-700 ring-1 ring-red-200'
          }`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

const TierBadge: React.FC<{ tier: Tier }> = ({ tier }) => (
  <span
    className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${TIERS[tier].className}`}
  >
    {TIERS[tier].label}
  </span>
);

// -----------------------------------------------------------------------------

const ForAgents: React.FC = () => {
  return (
    <div className="bg-surface">
      {/* ================================================================ */}
      {/* Hero                                                             */}
      {/* ================================================================ */}
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-brand-200 opacity-20 mix-blend-multiply blur-3xl filter" />
        <div className="absolute bottom-10 right-10 h-72 w-72 rounded-full bg-purple-200 opacity-20 mix-blend-multiply blur-3xl filter" />

        <div className="relative mx-auto max-w-4xl px-6 pb-20 pt-20 text-center sm:pt-28 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Green now. This pill marked something you could not use while the
                surface was read-only and half-built; it is neither now. */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5">
              <CpuChipIcon className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">
                Live — every client, every tool
              </span>
            </div>

            <h1 className="text-display text-balance text-content lg:text-display-lg">
              Your agent can search.{' '}
              <span className="gradient-text">It can't tell you you're ineligible.</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-content-muted">
              An MCP server and a REST API so Claude, ChatGPT, or your own code can reach the
              parts of federal contracting that aren't lookups — bid readiness, set-aside
              eligibility, your pipeline, your proposal drafts, the solicitation documents
              themselves, and the analysis agents Clevor runs. You choose what each connection
              can reach, and whether it may spend your credits at all.
            </p>

            <div className="mt-10 flex justify-center">
              <Waitlist />
            </div>
            <p className="mt-4 text-xs text-content-subtle">
              Already have an account? Connect from{' '}
              <Link to="/settings?tab=api-keys" className="underline hover:text-content-muted">
                Settings → Agent Access
              </Link>
              . You pick the permissions; spending your AI credits is off unless you turn it on.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* What setup will look like                                        */}
      {/* ================================================================ */}
      <div className="bg-surface-sunk py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto mb-10 max-w-2xl text-center"
          >
            <h2 className="text-title-lg text-balance text-content sm:text-display">
              What connecting will look like
            </h2>
            <p className="mt-6 text-lg leading-8 text-content-muted">
              Add the server address in whichever app you use. It asks you to sign in and
              approve what it may read — there is no key to copy and nothing to keep secret.
            </p>
          </motion.div>

          {/* Deliberately not copyable and deliberately not a button. This
              endpoint does not exist; a copy control would hand someone a
              command that 404s and make this page a liar. */}
          <div className="overflow-hidden rounded-xl ring-1 ring-gray-800">
            <div className="flex items-center gap-3 bg-gray-800 px-4 py-2.5">
              <div className="flex gap-1.5">
                <div className="h-3 w-3 rounded-full bg-red-400" />
                <div className="h-3 w-3 rounded-full bg-yellow-400" />
                <div className="h-3 w-3 rounded-full bg-green-400" />
              </div>
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <CommandLineIcon className="h-3.5 w-3.5" />
                Claude Code
              </span>
            </div>
            <div className="overflow-x-auto bg-gray-900 px-5 py-6">
              <pre className="text-sm leading-7 text-gray-300">
                <code>
                  <span className="text-gray-500">$ </span>
                  claude mcp add --transport http clevor https://clevor.ai/api/mcp
                  {'\n'}
                  <span className="text-gray-500">$ </span>
                  claude{' '}
                  <span className="text-emerald-400">
                    "what's new this week that I'm actually eligible for?"
                  </span>
                  {'\n\n'}
                  <span className="text-gray-500">
                    {'  '}→ 34 new solicitations matched your NAICS codes
                    {'\n'}
                    {'  '}→ 9 passed the set-aside check
                    {'\n'}
                    {'  '}→ 2 need a NAICS code added to your SAM registration first
                    {'\n'}
                    {'  '}→ 1 closes Tuesday
                  </span>
                </code>
              </pre>
            </div>
          </div>

          <p className="mt-4 text-center text-sm text-content-subtle">
            The command is real. The numbers underneath it are an illustration, not a promise
            about your pipeline.
          </p>

          {/* ---------------------------------------------------------- */}
          {/* Which clients, and what each needs                          */}
          {/* ---------------------------------------------------------- */}
          <div className="mt-14">
            <h3 className="text-title text-content">Where it will work</h3>
            <p className="mt-2 text-body text-content-muted">
              One server, every client. Sign in wherever you can; a key is for what can't.
            </p>

            <div className="mt-5 overflow-hidden rounded-xl border border-border">
              {CLIENTS.map((client, i) => (
                <div
                  key={client.name}
                  className={`flex flex-col gap-1.5 px-4 py-3.5 sm:flex-row sm:items-center sm:gap-4 ${
                    i > 0 ? 'border-t border-border' : ''
                  }`}
                >
                  <p className="w-full flex-shrink-0 text-sm font-medium text-content sm:w-56">
                    {client.name}
                  </p>
                  <p className="flex-1 font-mono text-xs text-content-muted">{client.how}</p>
                  <span
                    className={`inline-flex flex-shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${
                      client.auth === 'oauth'
                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                        : 'bg-surface-sunk text-content-muted ring-border'
                    }`}
                  >
                    {client.auth === 'key' ? 'API key' : 'Sign in'}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-4 text-sm leading-relaxed text-content-subtle">
              Signing in is the better way in almost everywhere: you approve what the app may
              read, on a screen that shows you exactly where the codes will go, the token expires
              on its own, and you can disconnect it at any time from Settings. API keys exist for
              the one case sign-in can't cover — something running with no browser to sign in from.
              They're static secrets, so we default them to 90 days and keep them out of the way.
            </p>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Planned tools                                                    */}
      {/* ================================================================ */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto mb-14 max-w-2xl text-center"
          >
            <h2 className="text-title-lg text-balance text-content sm:text-display">
              {SPELLED[LIVE_TOOL_COUNT] ?? LIVE_TOOL_COUNT} tools, live now
            </h2>
            <p className="mt-6 text-lg leading-8 text-content-muted">
              Fewer than you'll find elsewhere, and pointed somewhere else. Searching a contract
              database is a solved problem your agent can already do. Knowing whether you can
              legally bid the thing it found is not. Most of these read and cost nothing. Three
              spend an AI credit each — the same agents the app runs, at the same price — and they
              only work if you tick that permission when you connect, with a daily ceiling you
              set. Nothing here sends anything outside your account.
            </p>
          </motion.div>

          <div className="space-y-10">
            {TOOL_GROUPS.map((group, gi) => (
              <motion.div
                key={group.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: gi * 0.05 }}
              >
                <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h3 className="text-title text-content">{group.name}</h3>
                  <p className="text-sm text-content-muted">{group.note}</p>
                </div>
                <div className="overflow-hidden rounded-xl border border-border">
                  {group.tools.map((tool, ti) => (
                    <div
                      key={tool.name}
                      className={`flex flex-col gap-1.5 px-4 py-3.5 sm:flex-row sm:items-center sm:gap-4 ${
                        ti > 0 ? 'border-t border-border' : ''
                      }`}
                    >
                      <code className="w-full flex-shrink-0 font-mono text-sm text-content sm:w-64">
                        {tool.name}
                      </code>
                      <p className="flex-1 text-sm text-content-muted">{tool.desc}</p>
                      <TierBadge tier={tool.tier} />
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Guardrails                                                       */}
      {/* ================================================================ */}
      <div className="bg-surface-sunk py-24 sm:py-32">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto mb-14 max-w-2xl text-center"
          >
            <h2 className="text-title-lg text-balance text-content sm:text-display">
              The guardrails are the hard part
            </h2>
            <p className="mt-6 text-lg leading-8 text-content-muted">
              An agent asking Clevor a question is you asking it — same account, same intent. What
              changes is arithmetic: one sentence becomes forty calls, and a client answers a
              failure by trying again rather than by stopping.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {GUARDRAILS.map((g, i) => (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-2xl border border-border bg-surface p-6"
              >
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50">
                  <g.icon className="h-5 w-5 text-brand-600" aria-hidden="true" />
                </div>
                <h3 className="text-title text-content">{g.title}</h3>
                <p className="mt-2 text-body leading-relaxed text-content-muted">{g.body}</p>
              </motion.div>
            ))}
          </div>

          {/* The honest footnote. Competitors describe client-side approval as
              though it were a server-side guarantee; it is not, and saying so
              is cheap for us and useful to anyone evaluating either product. */}
          <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
            <div className="flex gap-4">
              <ShieldCheckIcon className="h-6 w-6 flex-shrink-0 text-amber-600" aria-hidden="true" />
              <div>
                <h3 className="text-title text-content">
                  One thing worth knowing about "confirm before change"
                </h3>
                <p className="mt-2 text-body leading-relaxed text-content-muted">
                  When a tool asks your permission before writing, the thing asking is your AI
                  app — Claude, ChatGPT, Cursor — not the server it's talking to. Every one of
                  them has an "always allow" button, and once it's pressed the prompting stops.
                  No server can override that. It's why anything of ours that spends credits or
                  writes a proposal will wait for you inside Clevor instead, where we can
                  actually hold it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* CTA                                                              */}
      {/* ================================================================ */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-purple-700" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute left-1/4 top-10 h-96 w-96 rounded-full bg-surface blur-3xl filter" />
          <div className="absolute bottom-10 right-1/4 h-96 w-96 rounded-full bg-purple-300 blur-3xl filter" />
        </div>
        <div className="relative px-6 py-24 sm:py-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-2xl text-center"
          >
            <h2 className="text-title-lg text-balance text-white sm:text-display">
              Want it? Say so.
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-brand-100">
              We're building the order of these tools around who asks for what. Tell us what
              you'd point an agent at and it moves up.
            </p>
            <div className="mt-10 flex justify-center">
              <Waitlist dark />
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
              <span className="inline-flex items-center gap-1.5 text-brand-200">
                <LockClosedIcon className="h-4 w-4" />
                Nothing is connected to your account until you create a key
              </span>
              <span className="text-brand-300" aria-hidden="true">
                |
              </span>
              <Link
                to="/privacy"
                className="font-semibold text-white underline underline-offset-2 transition-colors hover:text-brand-100"
              >
                Privacy Policy
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ForAgents;
